import * as THREE from 'three'
import type {
  ExtendedWebGL2RenderingContext,
  ExtendedWebGLRenderer,
  MutableDataTexture,
  TextureProperties,
} from '@/plugins/gsplat/types/WebGLTypes'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:utils')

// ============================================================================
// 类型定义
// ============================================================================

/**
 * 支持的类型化数组类型
 */
export type TypedArray = Uint8Array | Uint16Array | Uint32Array | Float32Array

/**
 * GPU 资源目标类型
 * - WebGL: THREE.DataTexture
 * - WebGPU: 未来可能支持 StorageBuffer
 */
export type UploadTarget = THREE.DataTexture

export interface UploadRegion {
  /** 源 typed array 的元素偏移 */
  sourceOffset?: number
  /** 目标纹理的元素偏移，必须按行对齐 */
  targetOffset?: number
  /** 上传元素数量，必须按行对齐 */
  size?: number
}

// ============================================================================
// 抽象接口
// ============================================================================

/**
 * UploadStream 抽象接口
 *
 * 定义 GPU 数据上传流的统一接口，支持不同的后端实现：
 * - WebGL: 使用 PBO (Pixel Buffer Object) 实现非阻塞纹理上传
 * - WebGPU: (预留) 使用 Staging Buffer 实现非阻塞缓冲区上传
 *
 * @category Utils
 */
export interface IUploadStream {
  /**
   * 上传数据到 GPU 资源
   *
   * @param data - 要上传的数据
   * @param target - 目标 GPU 资源
   * @param offset - 目标中的元素偏移量，默认为 0
   * @param size - 要上传的元素数量，默认为 data.length
   */
  upload(data: TypedArray, target: UploadTarget, offset?: number, size?: number): void

  /**
   * 上传源数组中的指定区间到目标纹理的指定区间。
   *
   * @param data - 源数据
   * @param target - 目标纹理
   * @param region - 源/目标区间，元素单位
   */
  uploadRegion(data: TypedArray, target: UploadTarget, region: UploadRegion): void

  /**
   * 销毁上传流并释放所有资源
   */
  destroy(): void
}

/**
 * UploadStream 配置选项
 */
export interface UploadStreamOptions {
  /**
   * 是否使用单缓冲模式
   * - true: 简单直接上传（会阻塞）
   * - false: 使用多缓冲策略实现非阻塞上传（默认）
   */
  useSingleBuffer?: boolean

  /**
   * Enable expensive GL error polling after texture uploads.
   * Keep this disabled in production because gl.getError() can force a GPU sync on mobile.
   */
  debugGlErrors?: boolean
}

// ============================================================================
// WebGL 实现
// ============================================================================

/**
 * PBO (Pixel Buffer Object) 信息
 * @internal
 */
interface PBOInfo {
  pbo: WebGLBuffer
  size: number
}

/**
 * 等待中的 PBO 信息（包含同步对象）
 * @internal
 */
interface PendingPBO extends PBOInfo {
  sync: WebGLSync
}

/**
 * WebGL 实现的 UploadStream
 *
 * 使用 PBO (Pixel Buffer Object) 实现非阻塞纹理上传：
 * - Orphaning 模式避免 GPU 同步等待
 * - Fence Sync 异步检测上传完成
 * - Buffer Pool 复用缓冲区减少分配
 *
 * @category Utils
 */
export class WebGLUploadStream implements IUploadStream {
  /** WebGL2 上下文（扩展类型，包含 getTexLevelParameter） */
  private gl: ExtendedWebGL2RenderingContext

  /** Three.js 渲染器 */
  private renderer: THREE.WebGLRenderer

  /** 可用的 PBO 池 */
  private availablePBOs: PBOInfo[] = []

  /** GPU 正在使用的 PBO 列表 */
  private pendingPBOs: PendingPBO[] = []

  /** 是否使用单缓冲模式 */
  private useSingleBuffer: boolean

  private debugGlErrors: boolean

  private directTextures = new WeakMap<THREE.DataTexture, WebGLTexture>()

  private directDisposeHandlers = new WeakMap<THREE.DataTexture, () => void>()

  private directTextureTargets = new Set<THREE.DataTexture>()

  /** 设备丢失事件处理器 */
  private onContextLostHandler: ((event: Event) => void) | null = null

  /**
   * 创建 WebGL UploadStream 实例
   *
   * @param renderer - Three.js WebGL 渲染器
   * @param options - 配置选项
   */
  constructor(renderer: THREE.WebGLRenderer, options: UploadStreamOptions = {}) {
    this.renderer = renderer
    this.useSingleBuffer = options.useSingleBuffer ?? false
    this.debugGlErrors =
      options.debugGlErrors ??
      (globalThis as { __SHARE3D_DEBUG_UPLOAD_GL_ERRORS__?: boolean })
        .__SHARE3D_DEBUG_UPLOAD_GL_ERRORS__ === true

    // 获取 WebGL2 上下文（使用扩展类型）
    const gl = renderer.getContext() as ExtendedWebGL2RenderingContext
    if (!gl) {
      throw new Error('WebGLUploadStream requires WebGL2 context')
    }
    this.gl = gl

    // 注册设备丢失事件监听
    this.onContextLostHandler = this._onDeviceLost.bind(this)
    renderer.domElement.addEventListener('webglcontextlost', this.onContextLostHandler)
  }

  /**
   * 销毁上传流并清理所有池化的资源
   */
  destroy(): void {
    // 移除事件监听
    if (this.onContextLostHandler) {
      this.renderer.domElement.removeEventListener('webglcontextlost', this.onContextLostHandler)
      this.onContextLostHandler = null
    }

    // 清理所有 PBO 和同步对象
    const gl = this.gl
    this.availablePBOs.forEach((info) => gl.deleteBuffer(info.pbo))
    this.pendingPBOs.forEach((item) => {
      if (item.sync) gl.deleteSync(item.sync)
      gl.deleteBuffer(item.pbo)
    })

    for (const texture of Array.from(this.directTextureTargets)) {
      this.deleteDirectTexture(texture)
    }

    this.availablePBOs = []
    this.pendingPBOs = []
    this.directTextures = new WeakMap<THREE.DataTexture, WebGLTexture>()
    this.directDisposeHandlers = new WeakMap<THREE.DataTexture, () => void>()
    this.directTextureTargets.clear()
  }

  /**
   * 上传数据到纹理
   *
   * @param data - 要上传的数据，必须至少包含 size 个元素
   * @param target - 目标纹理
   * @param offset - 目标中的元素偏移量，默认为 0。必须是纹理宽度的倍数
   * @param size - 要上传的元素数量，默认为 data.length。必须是纹理宽度的倍数
   */
  upload(
    data: TypedArray,
    target: THREE.DataTexture,
    offset: number = 0,
    size: number = data.length
  ): void {
    this.uploadRegion(data, target, {
      sourceOffset: 0,
      targetOffset: offset,
      size,
    })
  }

  uploadRegion(data: TypedArray, target: THREE.DataTexture, region: UploadRegion): void {
    const sourceOffset = Math.max(0, Math.floor(region.sourceOffset ?? 0))
    const targetOffset = Math.max(0, Math.floor(region.targetOffset ?? 0))
    const size = Math.max(0, Math.floor(region.size ?? data.length - sourceOffset))
    this.validateSourceRange(data, sourceOffset, size)

    const targetLength = (target.image?.data as TypedArray | undefined)?.length ?? 0
    const canUseDirect =
      this.useSingleBuffer && targetOffset === 0 && sourceOffset === 0 && size === targetLength

    if (canUseDirect) {
      this.uploadDirect(data, target, sourceOffset, targetOffset, size)
    } else {
      this.uploadPBO(data, target, sourceOffset, targetOffset, size)
    }
  }

  private validateSourceRange(data: TypedArray, sourceOffset: number, size: number): void {
    if (sourceOffset < 0 || size < 0 || sourceOffset + size > data.length) {
      throw new Error(
        `Upload source range out of bounds: sourceOffset=${sourceOffset}, size=${size}, length=${data.length}`
      )
    }
  }

  /**
   * 直接纹理上传（简单，阻塞）
   */
  private uploadDirect(
    data: TypedArray,
    target: THREE.DataTexture,
    sourceOffset: number,
    targetOffset: number,
    size: number
  ): void {
    if (!target.image || !target.image.data) {
      throw new Error('Target texture must have image.data')
    }

    if (targetOffset !== 0 || size !== target.image.data.length) {
      throw new Error(
        'Direct texture upload only supports full texture replacement. Use PBO mode instead.'
      )
    }

    if (!(sourceOffset === 0 && size === target.image.data.length && data === target.image.data)) {
      target.image.data.set(data.subarray(sourceOffset, sourceOffset + size), 0)
    }

    const gl = this.gl
    const width = target.image.width
    const height = target.image.height
    const glTexture = this.ensureDirectTexture(target)
    this.bindTexture(glTexture)

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, data.BYTES_PER_ELEMENT)
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0)
    gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0)
    gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0)

    this.applyTextureParameters(target)

    const glFormat = this.getGLFormat(target.format as THREE.PixelFormat)
    const glType = this.getGLType(target.type)
    const glInternalFormat = this.getGLInternalFormat(target, glFormat, glType)
    const uploadData = this.getUploadView(target.image.data as TypedArray, glType)

    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      glInternalFormat,
      width,
      height,
      0,
      glFormat,
      glType,
      uploadData
    )

    this.markTextureUploaded(target)
  }

  /**
   * 基于 PBO 的上传（优化，非阻塞）
   */
  private ensureDirectTexture(target: THREE.DataTexture): WebGLTexture {
    const existing = this.directTextures.get(target)
    if (existing) {
      return existing
    }

    const glTexture = this.gl.createTexture()
    if (!glTexture) {
      throw new Error('Failed to create direct WebGL texture')
    }

    const extRenderer = this.renderer as ExtendedWebGLRenderer
    const properties = extRenderer.properties?.get(target) as TextureProperties | undefined
    if (properties) {
      properties.__webglTexture = glTexture
      properties.__share3dUploadStreamDirect = true
    }

    this.directTextures.set(target, glTexture)
    this.directTextureTargets.add(target)

    const disposeHandler = () => this.deleteDirectTexture(target)
    this.directDisposeHandlers.set(target, disposeHandler)
    target.addEventListener('dispose', disposeHandler)

    return glTexture
  }

  private deleteDirectTexture(target: THREE.DataTexture): void {
    const glTexture = this.directTextures.get(target)
    if (glTexture) {
      this.gl.deleteTexture(glTexture)
      this.directTextures.delete(target)
    }

    const disposeHandler = this.directDisposeHandlers.get(target)
    if (disposeHandler) {
      target.removeEventListener('dispose', disposeHandler)
      this.directDisposeHandlers.delete(target)
    }

    this.directTextureTargets.delete(target)

    const extRenderer = this.renderer as ExtendedWebGLRenderer
    const properties = extRenderer.properties?.get(target) as TextureProperties | undefined
    if (properties?.__share3dUploadStreamDirect) {
      properties.__webglTexture = undefined
      properties.__version = undefined
      properties.__share3dUploadStreamDirect = false
    }
  }

  private bindTexture(glTexture: WebGLTexture): void {
    const rendererState = (
      this.renderer as unknown as {
        state?: {
          bindTexture?: (target: number, texture: WebGLTexture, slot?: number) => void
          activeTexture?: (slot: number) => void
        }
      }
    ).state

    rendererState?.activeTexture?.(this.gl.TEXTURE0)
    if (rendererState?.bindTexture) {
      rendererState.bindTexture(this.gl.TEXTURE_2D, glTexture, this.gl.TEXTURE0)
      return
    }

    this.gl.activeTexture(this.gl.TEXTURE0)
    this.gl.bindTexture(this.gl.TEXTURE_2D, glTexture)
  }

  private applyTextureParameters(target: THREE.DataTexture): void {
    const gl = this.gl
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, this.getGLWrapping(target.wrapS))
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, this.getGLWrapping(target.wrapT))
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, this.getGLFilter(target.magFilter))
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, this.getGLFilter(target.minFilter))
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, 0)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, 0)
  }

  private getUploadView(data: TypedArray, glType: number): TypedArray {
    const gl = this.gl
    if (glType === gl.UNSIGNED_BYTE && !(data instanceof Uint8Array)) {
      return new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    }
    if (
      (glType === gl.UNSIGNED_SHORT || glType === gl.HALF_FLOAT) &&
      !(data instanceof Uint16Array)
    ) {
      return new Uint16Array(
        data.buffer,
        data.byteOffset,
        data.byteLength / Uint16Array.BYTES_PER_ELEMENT
      )
    }
    if (glType === gl.UNSIGNED_INT && !(data instanceof Uint32Array)) {
      return new Uint32Array(
        data.buffer,
        data.byteOffset,
        data.byteLength / Uint32Array.BYTES_PER_ELEMENT
      )
    }
    if (glType === gl.FLOAT && !(data instanceof Float32Array)) {
      return new Float32Array(
        data.buffer,
        data.byteOffset,
        data.byteLength / Float32Array.BYTES_PER_ELEMENT
      )
    }
    return data
  }

  private uploadPBO(
    data: TypedArray,
    target: THREE.DataTexture,
    sourceOffset: number,
    targetOffset: number,
    size: number
  ): void {
    const gl = this.gl
    const width = target.image.width
    const height = target.image.height
    const byteSize = size * data.BYTES_PER_ELEMENT

    // 更新 PBO 池
    this.update(byteSize)

    // 行对齐校验
    if (targetOffset % width !== 0) {
      throw new Error(
        `Upload offset (${targetOffset}) must be a multiple of texture width (${width}) for row alignment`
      )
    }
    if (size % width !== 0) {
      throw new Error(
        `Upload size (${size}) must be a multiple of texture width (${width}) for row alignment`
      )
    }

    const startY = targetOffset / width
    const uploadHeight = size / width

    // 验证边界
    if (startY + uploadHeight > height) {
      log.error(
        `[UploadStream] texSubImage2D overflow: startY=${startY}, uploadHeight=${uploadHeight}, texture height=${height}, texture size=${width}x${height}, targetOffset=${targetOffset}, size=${size}`
      )
      throw new Error(
        `Upload exceeds texture bounds: startY(${startY}) + uploadHeight(${uploadHeight}) > height(${height})`
      )
    }

    // 获取或创建 PBO
    const pboInfo =
      this.availablePBOs.pop() ??
      (() => {
        const pbo = gl.createBuffer()
        if (!pbo) {
          throw new Error('Failed to create PBO')
        }
        return { pbo, size: byteSize }
      })()

    // Orphan + bufferSubData 模式
    gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, pboInfo.pbo)
    gl.bufferData(gl.PIXEL_UNPACK_BUFFER, byteSize, gl.STREAM_DRAW)
    gl.bufferSubData(
      gl.PIXEL_UNPACK_BUFFER,
      0,
      new Uint8Array(data.buffer, data.byteOffset + sourceOffset * data.BYTES_PER_ELEMENT, byteSize)
    )

    gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, null)

    this.ensureTextureAllocatedForSubImage(target)

    // 获取 WebGL 纹理对象
    const extRenderer = this.renderer as ExtendedWebGLRenderer
    const properties = extRenderer.properties?.get(target) as TextureProperties | undefined
    const glTexture = properties?.__webglTexture
    if (!glTexture) {
      throw new Error('Failed to get WebGL texture from Three.js')
    }

    // 固定使用纹理单元 0，并同步 Three.js 的纹理状态缓存。
    // 裸 bindTexture 会覆盖当前活跃的材质采样单元，导致后续 draw 使用错误纹理类型。
    this.bindTexture(glTexture)
    gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, pboInfo.pbo)

    // 设置像素存储参数
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, data.BYTES_PER_ELEMENT)
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0)
    gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0)
    gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0)

    // 获取格式和类型
    const glFormat = this.getGLFormat(target.format as THREE.PixelFormat)
    const glType = this.getGLType(target.type)

    // 从 PBO 复制到纹理
    try {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, startY, width, uploadHeight, glFormat, glType, 0)
      if (this.debugGlErrors) {
        const err = gl.getError()
        if (err !== gl.NO_ERROR) {
          let levelW = -1
          let levelH = -1
          let levelIF = -1
          try {
            levelW = gl.getTexLevelParameter(gl.TEXTURE_2D, 0, gl.TEXTURE_WIDTH) as number
            levelH = gl.getTexLevelParameter(gl.TEXTURE_2D, 0, gl.TEXTURE_HEIGHT) as number
            levelIF = gl.getTexLevelParameter(
              gl.TEXTURE_2D,
              0,
              gl.TEXTURE_INTERNAL_FORMAT
            ) as number
          } catch (_e) {
            // ignore
          }
          log.error(
            `[UploadStream] texSubImage2D glError=${err} tex=${target.name || 'unnamed'} fmt=${target.format} type=${target.type} internal=${(target as MutableDataTexture).internalFormat} width=${width} height=${height} startY=${startY} uploadH=${uploadHeight} targetOffset=${targetOffset} size=${size} glFormat=${glFormat} glType=${glType} levelSize=${levelW}x${levelH} levelIF=${levelIF}`
          )
          // 继续抛出，方便上层捕获并避免重复错误
          throw new Error('texSubImage2D failed')
        }
      }
    } catch (e) {
      log.error(
        `[UploadStream] texSubImage2D exception tex=${target.name || 'unnamed'} fmt=${target.format} type=${target.type} internal=${(target as MutableDataTexture).internalFormat} width=${width} height=${height} startY=${startY} uploadH=${uploadHeight} targetOffset=${targetOffset} size=${size}`
      )
      throw e
    }

    gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, null)

    // 创建同步对象
    const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0)
    if (!sync) {
      throw new Error('Failed to create sync object')
    }

    // 追踪 PBO 以便回收
    this.pendingPBOs.push({ pbo: pboInfo.pbo, size: byteSize, sync })

    // 触发 GPU 执行
    gl.flush()

    // 更新纹理版本
    this.markTextureUploaded(target)
  }

  private markTextureUploaded(target: THREE.DataTexture): void {
    const mutableTarget = target as MutableDataTexture
    const nextVersion = (mutableTarget.version ?? 0) + 1
    mutableTarget.version = nextVersion

    const extRenderer = this.renderer as ExtendedWebGLRenderer
    const properties = extRenderer.properties?.get(target) as TextureProperties | undefined
    if (properties) {
      properties.__version = nextVersion
    }

    const source = target.source
    const sourceProperties = extRenderer.properties?.get(source) as TextureProperties | undefined
    if (sourceProperties) {
      sourceProperties.__version = source.version
    }
  }

  private ensureTextureAllocatedForSubImage(target: THREE.DataTexture): void {
    const extRenderer = this.renderer as ExtendedWebGLRenderer
    const properties = extRenderer.properties?.get(target) as TextureProperties | undefined
    if (properties?.__webglTexture) {
      return
    }

    const source = target.source as { dataReady?: boolean } | undefined
    const previousDataReady = source?.dataReady
    if (source) {
      source.dataReady = false
    }

    try {
      target.needsUpdate = true
      this.renderer.initTexture(target)
    } finally {
      if (source) {
        source.dataReady = previousDataReady ?? true
      }
    }
  }

  /**
   * 更新 PBO 状态：轮询已完成的 PBO 并移除过小的缓冲区
   */
  private update(minByteSize: number): void {
    const gl = this.gl

    // 轮询等待中的 PBO
    const pending = this.pendingPBOs
    for (let i = pending.length - 1; i >= 0; i--) {
      const item = pending[i]
      const result = gl.clientWaitSync(item.sync, 0, 0)
      if (result === gl.CONDITION_SATISFIED || result === gl.ALREADY_SIGNALED) {
        gl.deleteSync(item.sync)
        this.availablePBOs.push({ pbo: item.pbo, size: item.size })
        pending.splice(i, 1)
      }
    }

    // 移除过小的可用 PBO
    const available = this.availablePBOs
    for (let i = available.length - 1; i >= 0; i--) {
      if (available[i].size < minByteSize) {
        gl.deleteBuffer(available[i].pbo)
        available.splice(i, 1)
      }
    }
  }

  /**
   * 处理设备丢失事件
   */
  private _onDeviceLost(_event: Event): void {
    this.availablePBOs.length = 0
    this.pendingPBOs.length = 0
  }

  /**
   * 将 Three.js 像素格式转换为 WebGL 格式常量
   */
  private getGLFormat(format: THREE.PixelFormat): number {
    const gl = this.gl
    switch (format) {
      case THREE.RGBAFormat:
        return gl.RGBA
      case THREE.RGBAIntegerFormat:
        return gl.RGBA_INTEGER
      case THREE.RGIntegerFormat:
        return gl.RG_INTEGER
      case THREE.RedIntegerFormat:
        return gl.RED_INTEGER
      case THREE.RGFormat:
        return gl.RG
      case THREE.RedFormat:
        return gl.RED
      default:
        throw new Error(`Unsupported texture format: ${format}`)
    }
  }

  /**
   * 将 Three.js 纹理数据类型转换为 WebGL 类型常量
   */
  private getGLType(type: THREE.TextureDataType): number {
    const gl = this.gl
    switch (type) {
      case THREE.UnsignedIntType:
        return gl.UNSIGNED_INT
      case THREE.HalfFloatType:
        return gl.HALF_FLOAT
      case THREE.FloatType:
        return gl.FLOAT
      case THREE.UnsignedByteType:
        return gl.UNSIGNED_BYTE
      case THREE.UnsignedShortType:
        return gl.UNSIGNED_SHORT
      default:
        throw new Error(`Unsupported texture type: ${type}`)
    }
  }

  private getGLInternalFormat(target: THREE.DataTexture, glFormat: number, glType: number): number {
    const gl = this.gl
    const explicit = (target as MutableDataTexture).internalFormat
    if (typeof explicit === 'string') {
      switch (explicit) {
        case 'R32UI':
          return gl.R32UI
        case 'RG32UI':
          return gl.RG32UI
        case 'RGBA32UI':
          return gl.RGBA32UI
        case 'RGBA16F':
          return gl.RGBA16F
        case 'RGBA8':
          return gl.RGBA8
        default:
          break
      }
    }

    if (glFormat === gl.RED_INTEGER && glType === gl.UNSIGNED_INT) return gl.R32UI
    if (glFormat === gl.RG_INTEGER && glType === gl.UNSIGNED_INT) return gl.RG32UI
    if (glFormat === gl.RGBA_INTEGER && glType === gl.UNSIGNED_INT) return gl.RGBA32UI
    if (glFormat === gl.RGBA && glType === gl.HALF_FLOAT) return gl.RGBA16F
    if (glFormat === gl.RGBA && glType === gl.UNSIGNED_BYTE) return gl.RGBA8

    return glFormat
  }

  private getGLWrapping(wrapping: THREE.Wrapping): number {
    const gl = this.gl
    switch (wrapping) {
      case THREE.RepeatWrapping:
        return gl.REPEAT
      case THREE.MirroredRepeatWrapping:
        return gl.MIRRORED_REPEAT
      default:
        return gl.CLAMP_TO_EDGE
    }
  }

  private getGLFilter(filter: THREE.TextureFilter): number {
    const gl = this.gl
    switch (filter) {
      case THREE.NearestFilter:
        return gl.NEAREST
      case THREE.NearestMipmapNearestFilter:
        return gl.NEAREST_MIPMAP_NEAREST
      case THREE.NearestMipmapLinearFilter:
        return gl.NEAREST_MIPMAP_LINEAR
      case THREE.LinearMipmapNearestFilter:
        return gl.LINEAR_MIPMAP_NEAREST
      case THREE.LinearMipmapLinearFilter:
        return gl.LINEAR_MIPMAP_LINEAR
      default:
        return gl.LINEAR
    }
  }
}

// ============================================================================
// WebGPU 实现（预留）
// ============================================================================

/**
 * WebGPU 实现的 UploadStream（预留接口）
 *
 * TODO: 未来实现 WebGPU 支持时，需要：
 * 1. 使用 Staging Buffer 替代 PBO
 * 2. 支持 StorageBuffer 作为上传目标
 * 3. 使用 copyBufferToBuffer 替代 texSubImage2D
 * 4. 对齐要求从行对齐改为 4 字节对齐
 *
 * @example
 * ```typescript
 * // 未来的 WebGPU 实现示意
 * export class WebGPUUploadStream implements IUploadStream {
 *     private device: GPUDevice;
 *     private availableStagingBuffers: GPUBuffer[] = [];
 *     private pendingStagingBuffers: GPUBuffer[] = [];
 *
 *     upload(data: TypedArray, target: GPUBuffer, offset = 0, size = data.length): void {
 *         // 使用 staging buffer + copyBufferToBuffer
 *     }
 * }
 * ```
 *
 * @category Utils
 */

// ============================================================================
// 工厂函数
// ============================================================================

/**
 * 后端类型
 */
export type UploadStreamBackend = 'webgl' | 'webgpu' | 'auto'

/**
 * 创建 UploadStream 实例
 *
 * 根据渲染器类型和配置自动选择合适的实现：
 * - WebGLRenderer: 使用 WebGLUploadStream
 * - WebGPURenderer: (未来) 使用 WebGPUUploadStream
 *
 * @param renderer - Three.js 渲染器
 * @param options - 配置选项
 * @returns IUploadStream 实例
 *
 * @example
 * ```typescript
 * // 自动选择后端
 * const uploadStream = createUploadStream(renderer);
 *
 * // 指定后端
 * const uploadStream = createUploadStream(renderer, { backend: 'webgl' });
 *
 * // 使用单缓冲模式
 * const uploadStream = createUploadStream(renderer, { useSingleBuffer: true });
 * ```
 */
export function createUploadStream(
  renderer: THREE.WebGLRenderer,
  options: UploadStreamOptions & { backend?: UploadStreamBackend } = {}
): IUploadStream {
  const { backend = 'webgl', ...streamOptions } = options

  // 目前只支持 WebGL
  // TODO: 未来添加 WebGPU 支持时，这里添加检测逻辑
  // if (backend === 'auto' || backend === 'webgpu') {
  //     if (renderer instanceof THREE.WebGPURenderer) {
  //         return new WebGPUUploadStream(renderer, streamOptions);
  //     }
  // }

  if (backend === 'webgpu') {
    log.warn('[UploadStream] WebGPU 后端未实现（WebGL2-only），回退到 webgl')
  }

  return new WebGLUploadStream(renderer, streamOptions)
}

// ============================================================================
// 兼容性导出（保持向后兼容）
// ============================================================================

/**
 * UploadStream 类（兼容别名）
 *
 * @deprecated 请使用 `createUploadStream()` 工厂函数或直接使用 `WebGLUploadStream`
 */
export const UploadStream = WebGLUploadStream
